import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import DraftReviewDialog, { ReasonBadges } from "@/components/collection/DraftReviewDialog";

const CARDS = [
  { k: "active_draft_count", label: "Invoice Draft (aktif)", hint: "Seluruh invoice aktif yang belum diverifikasi final" },
  { k: "needs_fat_verification_count", label: "Perlu Verifikasi FAT", card: "needs_fat", hint: "Draft yang menunggu tindakan FAT (bukan menunggu perbaikan pengaju)" },
  { k: "with_outstanding_count", label: "Draft bersisa", card: "with_outstanding", hint: "Draft dengan sisa > 0 — cakupan ringkasan A.R. Dasbor" },
  { k: "identity_incomplete_count", label: "Identitas belum lengkap", card: "identity_incomplete", hint: "Draft yang kehilangan identitas wajib" },
  { k: "draft_gross_total", label: "Bruto Draft", fmt: formatIDR, money: true, hint: "Nilai bruto seluruh draft aktif" },
  { k: "draft_cash_due_total", label: "Kas Seharusnya Diterima", fmt: formatIDR, money: true, hint: "Bruto − pajak − retensi" },
  { k: "draft_outstanding_total", label: "Sisa Draft", fmt: formatIDR, money: true, hint: "Kas seharusnya diterima − penerimaan tertaut (bukan piutang resmi)" },
  { k: "missing_invoice_number", label: "Tanpa nomor invoice", card: "missing_invoice_number" },
  { k: "missing_invoice_date", label: "Tanpa tanggal invoice", card: "missing_invoice_date" },
  { k: "missing_due_date", label: "Tanpa jatuh tempo", card: "missing_due_date" },
  { k: "missing_source", label: "Tanpa transaksi sumber", card: "missing_source" },
  { k: "duplicate_suspected", label: "Dugaan duplikat", card: "duplicate_suspected" },
];
const inp = "border border-slate-300 px-2 h-8 text-xs bg-white";

const Filters = ({ f, setF, labels }) => (
  <div className="flex flex-wrap gap-2 items-center" data-testid="draft-filter-bar">
    <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, card: "" })} className={inp} data-testid="draft-filter-category">
      <option value="">Semua</option>
      {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
    <input placeholder="Client" value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} className={`${inp} w-32`} data-testid="draft-filter-client" />
    <input placeholder="Proyek/WO" value={f.project} onChange={(e) => setF({ ...f, project: e.target.value })} className={`${inp} w-36`} data-testid="draft-filter-project" />
    <input placeholder="PIC" value={f.pic} onChange={(e) => setF({ ...f, pic: e.target.value })} className={`${inp} w-28`} data-testid="draft-filter-pic" />
    <input type="number" min={0} placeholder="Umur ≥ hari" value={f.min_age_days} onChange={(e) => setF({ ...f, min_age_days: e.target.value })} className={`${inp} w-28`} data-testid="draft-filter-age" />
    <select value={f.payment_state} onChange={(e) => setF({ ...f, payment_state: e.target.value })} className={inp} data-testid="draft-filter-payment">
      <option value="">Semua status bayar</option><option value="belum_dibayar">Belum Dibayar</option>
      <option value="dibayar_sebagian">Dibayar Sebagian</option><option value="lunas">Lunas</option>
    </select>
  </div>
);

const EMPTY = { category: "", card: "", client: "", project: "", pic: "", min_age_days: "", payment_state: "" };

/** Antrean kanonis Invoice Draft / Perlu Verifikasi (GET /collection/draft-queue). */
export default function DraftVerificationQueue({ onChanged, initialCard, onReset }) {
  const [f, setF] = useState({ ...EMPTY, card: initialCard || "" });
  const [data, setData] = useState({ items: [], summary: {}, category_labels: {} });
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const { data: d } = await api.get("/collection/draft-queue", { params });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat antrean draft."); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const s = data.summary || {};
  const fs = data.filtered_summary || {};
  return (
    <div className="space-y-3" data-testid="draft-queue">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {CARDS.map((c) => (
          <button key={c.k} type="button" disabled={!c.card} title={c.hint}
            onClick={() => { if (!c.card) return; setF({ ...EMPTY, card: f.card === c.card ? "" : c.card }); onReset?.(); }}
            className={`text-left border px-3 py-2 bg-white ${c.card && f.card === c.card ? "border-[#FF5A1F] ring-1 ring-[#FF5A1F]" : "border-slate-200"} ${c.card ? "hover:border-slate-400" : "cursor-default"}`}
            data-testid={`draft-card-${c.k}`}>
            <div className="text-[10px] uppercase text-slate-500">{c.label}</div>
            <div className={`font-bold ${c.money ? "text-sm" : "text-lg"} text-[#0B1D3A]`}>{(c.fmt || ((v) => v))(s[c.k] ?? 0)}</div>
          </button>
        ))}
        <div className="border border-slate-200 px-3 py-2 bg-white" data-testid="draft-card-oldest">
          <div className="text-[10px] uppercase text-slate-500">Tertua dalam antrean</div>
          <div className="text-xs font-semibold text-[#0B1D3A]">{s.oldest_draft_age_days != null ? `${s.oldest_draft_age_days} hari` : "—"}</div>
          <div className="text-[10px] text-slate-500 truncate">{s.oldest?.invoice_label}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Filters f={f} setF={setF} labels={data.category_labels || {}} />
        <div className="text-[11px] text-slate-600" data-testid="draft-filtered-summary">
          Ditampilkan {fs.active_draft_count ?? 0} invoice · kas seharusnya {formatIDR(fs.draft_cash_due_total || 0)} · sisa {formatIDR(fs.draft_outstanding_total || 0)}
          {Object.values(f).some((v) => v !== "") && <button onClick={() => { setF(EMPTY); onReset?.(); }} className="ml-2 text-sky-700 underline" data-testid="draft-clear">Reset</button>}
        </div>
      </div>
      <div className="border border-slate-200 bg-white overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-600"><tr className="text-left">
            <th className="px-2 py-2">Invoice</th><th className="px-2 py-2">Client · Proyek/WO</th><th className="px-2 py-2">Tgl Invoice</th>
            <th className="px-2 py-2 text-right">Kas Seharusnya</th><th className="px-2 py-2 text-right">Sisa</th><th className="px-2 py-2">Bayar · Verifikasi</th>
            <th className="px-2 py-2">Alasan</th><th className="px-2 py-2">Umur</th><th className="px-2 py-2" />
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="p-4 text-center text-slate-400">Memuat…</td></tr>
              : data.items.length === 0 ? <tr><td colSpan={9} className="p-4 text-center text-slate-400" data-testid="draft-empty">Tidak ada invoice draft.</td></tr>
              : data.items.map((r) => (
                <tr key={r.entry_key} className="border-t border-slate-100 align-top" data-testid={`draft-row-${r.entry_id}`}>
                  <td className="px-2 py-2"><div className={r.invoice_number ? "font-semibold" : "italic text-amber-700"}>{r.invoice_label}</div>{r.external_reference && <div className="text-[10px] text-slate-500">Ref: {r.external_reference}</div>}</td>
                  <td className="px-2 py-2"><div>{r.client || "—"}</div><div className="text-[10px] text-slate-500">{r.project_label}{r.wo_number ? ` · WO ${r.wo_number}` : ""}</div></td>
                  <td className="px-2 py-2">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatIDR(r.cash_due)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatIDR(r.draft_outstanding)}</td>
                  <td className="px-2 py-2 text-[10px]"><div>{r.payment_state_label}</div><div className="text-amber-700">{r.verification_state_label}</div></td>
                  <td className="px-2 py-2 max-w-xs"><ReasonBadges reasons={r.reasons} /></td>
                  <td className="px-2 py-2">{r.queue_age_days ?? "—"} hr</td>
                  <td className="px-2 py-2"><button onClick={() => setTarget(r)} className="px-2 py-1 bg-[#0B1D3A] text-white text-[10px] uppercase" data-testid={`draft-open-${r.entry_id}`}>Periksa</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <DraftReviewDialog open={!!target} target={target} canComplete={data.can_complete_identity}
        onClose={() => setTarget(null)} onChanged={() => { load(); onChanged?.(); }} />
    </div>
  );
}
